import type { Me } from "@riea-water/contracts";
import { useEffect, useRef } from "react";
import { useOutletContext } from "react-router";
import { useRuleRuns, useStartRun } from "../api/queries.js";
import type { ShellContext } from "../shell/Shell.js";
import { useToast } from "../shell/toast.js";

export function greeting(name: string, hour: number): string {
  const first = name.split(/\s+/)[0] ?? name;
  const part = hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening";
  return `Good ${part}, ${first}`;
}

const CAN_RUN: readonly Me["role"][] = ["tenant_admin", "data_lead", "analyst"];

const time = (iso: string) =>
  new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

/**
 * The landing screen. The header carries the last rule run and the Run rules
 * button as the prototype draws it (D-50); the aggregates arrive in Phase 6.
 */
export function Overview() {
  const { me, client } = useOutletContext<ShellContext>();
  const runs = useRuleRuns(client);
  const start = useStartRun(client);
  const { toast, Toast } = useToast();
  const last = runs.data?.[0];
  const watching = useRef<string | null>(null);

  // A run started from here is announced when it completes.
  useEffect(() => {
    const run = runs.data?.find((r) => r.id === watching.current);
    if (!run || run.status === "running") return;
    watching.current = null;
    toast(
      run.status === "completed"
        ? `Rule run ${run.ref} completed · ${run.counts.raised} raised, ${run.counts.updated} updated, ${run.counts.autoCleared} auto-cleared · RuleRunCompleted recorded`
        : `Rule run ${run.ref} failed · ${run.error ?? "see the run"}`,
    );
  }, [runs.data, toast]);

  const onRun = () => {
    start.mutate(undefined, {
      onSuccess: (run) => {
        watching.current = run.id;
        toast(
          `Rule run ${run.ref} started · ${run.ruleIds.length} rules · results in about a minute`,
        );
      },
      onError: (error) => toast(`Run refused · ${error.message}`),
    });
  };

  return (
    <>
      <div className="head">
        <div>
          <div className="eyebrow">Water Market · Overview</div>
          <h1>{greeting(me.name, new Date().getHours())}</h1>
        </div>
        <div className="split">
          <span className="muted" style={{ fontSize: ".8rem" }}>
            {last
              ? `Last rule run ${last.ref} ${last.status === "running" ? "running" : time(last.finishedAt ?? last.startedAt)} · data as of ${time(last.dataAsOf)}`
              : "No rule run yet"}
          </span>
          {CAN_RUN.includes(me.role) ? (
            <button
              className="btn primary sm"
              type="button"
              disabled={start.isPending || last?.status === "running"}
              onClick={onRun}
            >
              Run rules
            </button>
          ) : null}
        </div>
      </div>
      <section className="card">
        <header>
          <h2>Nothing to show yet</h2>
          <span className="pill info">Phase 6</span>
        </header>
        <div className="empty">
          The six indicators, findings by rule, long unread meters by readability, your team&apos;s
          work for today and the connections summary appear here in Phase 6. Findings and Rules are
          live in the navigation.
        </div>
      </section>
      <Toast />
    </>
  );
}
